import React, { useEffect, useState } from 'react'
import { useQuery } from '@apollo/client'
import { Paragraph, Button, colors, Separator, H5, H6 } from '@fs/zion-ui'
import { Link } from '@fs/zion-router'
import { Title } from '@fs/zion-head'
import { Trans } from 'react-i18next'
import { i18n } from '@fs/zion-locale'
import { useSearchResultsPageTitle } from 'hooks'
import { getLifeEvents } from '../FormInputs/queries'
import transl from './SearchSuggestions.trans'
import titleTrans from '../../views/SearchResultsPage/SearchResultsPage.transl'

const FIELD_ORDER = [
  'naturalGroupIds',
  'imageGroupNumbers',
  'keyword',
  'creator',
  'custodian',
  'cameraOperatorId',
  'mediaNumber',
  'projectId',
  'recordTypeIds',
  'startDate',
  'lifeEvent',
  'place',
  'map',
  'collectionIds',
]

function setTitle(searchCriteria) {
  const titleImportance = [
    'needsToBeOrganized',
    'startDateAvailable',
    'surname',
    'ancestralHall',
    'firstAncestor',
    'generationPoem',
    'imageGroupNumbers',
    'creator',
    'custodian',
    'cameraOperatorId',
    'keyword',
    'projectId',
    'naturalGroupIds',
    'apid',
    'ark',
    'titleNumber',
  ]

  const { key } = searchCriteria.reduce(
    (obj, item) => {
      const i = titleImportance.indexOf(item)
      if (i > -1 && i < obj.idx) {
        obj.idx = i
        obj.key = item
      }

      return obj
    },
    { idx: titleImportance.length, key: 'default' }
  )
  return key
}

const bold = <span css={{ fontWeight: 'bold' }} />
const lifeEventSuggestion = (
  <Trans i18nKey="lifeEventSuggestion" defaults={transl.lifeEventSuggestion} components={{ bold }} />
)
const recordTypeSuggestion = (
  <Trans i18nKey="recordTypeSuggestion" defaults={transl.recordTypeSuggestion} components={{ bold }} />
)

export default function SearchSuggestions({
  handleMapSuggestionClicked,
  place,
  searchCriteria,
  selectedFields,
  updateSearchCriteria,
  setValue,
}) {
  const linkUrl = `https://community.familysearch.org/${i18n.language}/categories/gp-groups-${i18n.language}`
  const [metadata, setMetadata] = useState('')
  const { data } = useQuery(getLifeEvents, { errorPolicy: 'all' })
  const searchResultsPageTitle = useSearchResultsPageTitle({ place })

  let count = 0

  useEffect(() => {
    const tempTitle = setTitle(Object.keys(searchCriteria))
    if (tempTitle === 'default') {
      const selectedLifeEvent = data?.lifeEvents?.filter((lifeEvent) => {
        return lifeEvent?.key === searchCriteria.lifeEvent
      })
      const titleArray = [place?.fullName, selectedFields?.date?.original, selectedLifeEvent?.[0]?.primaryText]
      const fullTitle = titleArray.filter((el) => {
        return el !== undefined
      })
      setMetadata(fullTitle.join(' | ') || titleTrans.defaultSearch)
    } else if (['ancestralHall', 'firstAncestor', 'generationPoem'].includes(tempTitle)) {
      setMetadata(searchCriteria[tempTitle])
    } else {
      setMetadata(titleTrans[`${tempTitle}Search`])
    }
  }, [data, place, searchCriteria, selectedFields])

  const showField = (heading, body, paramTerm, buttonText, newValue, children) => {
    count++

    return (
      <div key={heading}>
        <H6>{`${count}${transl.period()} ${heading}`}</H6>
        <Paragraph size="sm">{body}</Paragraph>
        <Button
          emphasis="medium"
          onClick={
            buttonText === transl.dateButton
              ? () => {
                  if (typeof setValue === 'function') {
                    setValue('startDate', null)
                    setValue('endDate', null)
                    setValue('date', null)
                  }

                  updateSearchCriteria({ startDate: null, endDate: null })
                }
              : () => {
                  updateSearchCriteria({ [paramTerm]: newValue })
                  setValue?.(paramTerm, newValue)
                }
          }
        >
          {buttonText || `${transl.removeButton()} ${heading}`}
        </Button>
        {children}
        <Separator size="md" />
      </div>
    )
  }

  const checkDate = () => {
    const today = new Date()
    const todayYear = today.getFullYear()
    if ('startDate' in searchCriteria) {
      if ('startDate' in searchCriteria > todayYear - 20) {
        return showField(transl.dateTitle, transl.dateTooSoonSuggestion, 'startDate', transl.dateButton)
      }
      return showField(transl.dateTitle, transl.dateSuggestion, 'startDate', transl.dateButton)
    }
    return ''
  }

  const getPlace = () => {
    const nextParent = place?.parents?.[0]
    if (!nextParent) return null
    const nextParentName = nextParent?.name
    const nextParentId = nextParent?.id
    const currentPlace = place?.name
    const buttonText = transl.placeAction(nextParentName)

    return showField(
      transl.placeTitle(),
      transl.placeSuggestion(nextParentName, currentPlace),
      'place',
      buttonText,
      nextParentId
    )
  }

  const getMap = () => {
    if (searchCriteria.place) {
      count++
      return (
        <React.Fragment key="map">
          <H6>{`${count}${transl.period()} ${transl.mapTitle()}`}</H6>
          <Paragraph size="sm">{transl.mapSuggestion()}</Paragraph>
          <Button emphasis="medium" onClick={handleMapSuggestionClicked}>
            {transl.mapButton()}
          </Button>
          <Separator size="md" />
        </React.Fragment>
      )
    }
    return null
  }

  const FIELDS = {
    naturalGroupIds: [
      transl.naturalGroupIdsTitle,
      transl.naturalGroupIdsSuggestion,
      'naturalGroupIds',
      transl.naturalGroupIdsButton,
    ],
    imageGroupNumbers: [
      transl.imageGroupNumberTitle,
      transl.imageGroupNumberSuggestion,
      'imageGroupNumbers',
      transl.imageGroupNumberButton,
    ],
    keyword: [transl.keywordTitle(), transl.keywordSuggestion(), 'keyword', transl.keywordButton()],
    creator: [transl.creatorTitle, transl.creatorSuggestion, 'creator', transl.creatorButton],
    custodian: [transl.custodianTitle, transl.custodianSuggestion, 'custodian', transl.custodianButton()],
    cameraOperatorId: [
      transl.cameraOperatorTitle,
      transl.cameraOperatorSuggestion,
      'cameraOperatorId',
      transl.cameraOperatorButton,
    ],
    mediaNumber: [transl.mediaNumberTitle, transl.mediaNumberSuggestion, 'mediaNumber', transl.mediaNumberButton],
    projectId: [transl.projectIdTitle, transl.projectIdSuggestion, 'projectId', transl.projectIdButton],
    recordTypeIds: [transl.recordTypeTitle, recordTypeSuggestion, 'recordTypeIds', transl.recordTypeButton],
    startDate: checkDate,
    lifeEvent: [transl.lifeEventTitle, lifeEventSuggestion, 'lifeEvent', transl.lifeEventButton],
    place: getPlace,
    map: getMap,
    collectionIds: [
      transl.collectionIdsTitle,
      transl.collectionIdsSuggestion,
      'collectionIds',
      transl.collectionIdsButton,
    ],
  }

  const shouldShowSuggestions = () => {
    const suggestions = []

    FIELD_ORDER.forEach((field) => {
      const args = FIELDS[field]

      if (typeof args === 'function') {
        suggestions.push(args())
      } else if (field in searchCriteria) {
        suggestions.push(showField(...args))
      }
    })

    return suggestions
  }

  return (
    <div
      css={{
        background: colors.gray03,
        borderRadius: '16px',
        marginTop: '24px',
        marginInlineEnd: '20px',
        marginBottom: '40px',
        marginInlineStart: '40px',
        padding: '20px 30px',
      }}
    >
      {!searchResultsPageTitle && <Title breadcrumbs={transl.exploreImages}>{metadata}</Title>}
      <H5>{transl.noResultsHeading()}</H5>
      <Separator size="xs" />
      {shouldShowSuggestions()}
      <H6>{transl.communityTitle()}</H6>
      <Paragraph size="sm">
        <Trans
          i18nKey="joinACommunityParagraph"
          defaults={transl.communityParagraph()}
          components={{
            communityLink: <Link to={linkUrl} external target="_blank" />,
          }}
        />
      </Paragraph>
    </div>
  )
}
